/**
 * GTM Performance Optimizer
 * 
 * This module optimizes Google Tag Manager performance by:
 * 1. Preventing duplicate gtm.config events
 * 2. Optimizing scroll depth tracking
 * 3. Blocking unnecessary history change events
 * 4. Debouncing scroll events to prevent excessive firing
 */

class GTMOptimizer {
    constructor() {
        this.gtmConfigured = false;
        this.scrollDepthTracked = false;
        this.scrollTimeout = null;
        this.lastScrollDepth = 0;
        this.initialized = false;
    }

    /**
     * Initialize the GTM optimizer
     */
    init() {
        if (this.initialized) return;
        this.initialized = true;

        // Override history methods immediately
        this.overrideHistoryMethods();
        
        // Start monitoring for GTM
        this.initializeGTM();
        
        console.log('GTM Performance: Optimizer initialized');
    }

    /**
     * Override history methods to prevent automatic GTM tracking
     */
    overrideHistoryMethods() {
        const originalPushState = history.pushState;
        const originalReplaceState = history.replaceState;
        const self = this;

        history.pushState = function(state, title, url) {
            const result = originalPushState.call(history, state, title, url);
            if (window.dataLayer && !self.gtmConfigured) {
                self.configureGTM();
            }
            return result;
        };

        history.replaceState = function(state, title, url) {
            const result = originalReplaceState.call(history, state, title, url);
            if (window.dataLayer && !self.gtmConfigured) {
                self.configureGTM();
            }
            return result;
        };
    }

    /**
     * Configure GTM with optimized settings
     */
    configureGTM() {
        if (this.gtmConfigured || !window.dataLayer) return;
        
        this.gtmConfigured = true;
        
        // Single configuration push with all settings
        window.dataLayer.push({
            'event': 'gtm.config',
            'gtm.allowlist': [
                'gtm.load', 'gtm.js', 'gtm.dom', 'gtm.click', 'gtm.submit',
                'gtm.scroll', 'gtm.visibility', 'gtm.formSubmit', 'gtm.linkClick'
            ],
            'gtm.blocklist': [
                'gtm.historyChange', 'gtm.historyChange-v2', 'gtm.historyChange-v3'
            ]
        });

        // Optimize scroll depth tracking
        this.optimizeScrollTracking();
        
        console.log('GTM Performance: Optimized configuration applied');
    }

    /**
     * Optimize scroll tracking to prevent multiple events
     */
    optimizeScrollTracking() {
        if (this.scrollDepthTracked) return;
        this.scrollDepthTracked = true;

        // Debounced scroll event handler
        window.addEventListener('scroll', () => {
            clearTimeout(this.scrollTimeout);
            this.scrollTimeout = setTimeout(() => {
                this.handleScrollDepth();
            }, 100); // 100ms debounce
        }, { passive: true });
    }

    /**
     * Handle scroll depth tracking with optimized thresholds
     */
    handleScrollDepth() {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const docHeight = document.documentElement.scrollHeight - window.innerHeight;
        const scrollPercent = Math.round((scrollTop / docHeight) * 100);
        
        // Only track significant scroll depth changes (every 25%)
        if (scrollPercent >= 25 && scrollPercent > this.lastScrollDepth + 24) {
            this.lastScrollDepth = scrollPercent;
            
            // Push custom scroll depth event instead of multiple GTM events
            if (window.dataLayer) {
                window.dataLayer.push({
                    'event': 'custom_scroll_depth',
                    'scroll_percent': scrollPercent,
                    'scroll_position': scrollTop,
                    'timestamp': Date.now()
                });
            }
        }
    }

    /**
     * Initialize GTM configuration when dataLayer becomes available
     */
    initializeGTM() {
        if (window.dataLayer && window.dataLayer.push) {
            this.configureGTM();
        } else {
            setTimeout(() => this.initializeGTM(), 50);
        }
    }

    /**
     * Manual method to force GTM configuration
     */
    forceConfigure() {
        if (window.dataLayer) {
            this.configureGTM();
        }
    }

    /**
     * Get current GTM optimization status
     */
    getStatus() {
        return {
            initialized: this.initialized,
            gtmConfigured: this.gtmConfigured,
            scrollDepthTracked: this.scrollDepthTracked
        };
    }
}

// Create singleton instance
const gtmOptimizer = new GTMOptimizer();

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => gtmOptimizer.init());
} else {
    gtmOptimizer.init();
}

// Export for manual use
export default gtmOptimizer;

// Also make available globally for debugging
if (typeof window !== 'undefined') {
    window.gtmOptimizer = gtmOptimizer;
}
